import React, { useState, useEffect } from 'react';
import {
    Calendar,
    Hash,
    Clock,
    Edit2,
    Save,
    X,
    FileText,
    CheckCircle,
    AlertCircle,
    Send,
    CalendarDays,
    FileEdit,
    Loader2
} from 'lucide-react';
import './CallForPapers.css';
import { ApiUrl } from '../API/ApiUrl';
import Toast from "../components/Toast/Toast";

const CallForPapers = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [marqueeText, setMarqueeText] = useState('');
    const [displayedMarquee, setDisplayedMarquee] = useState('');
    const [marqueeId, setMarqueeId] = useState(null);
    const [isMarqueeEditing, setIsMarqueeEditing] = useState(false);
    // Single Call for Papers data
    // const [callData, setCallData] = useState({});
    const [toast, setToast] = useState({
        show: false,
        message: "",
        type: "success"
    });
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        call_title: "",
        start_date: "",
        volume: "",
        issue: "",
        submission_deadline: "",
        review_date: "",
        publication_date: "",
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "volume" || name === "issue") {
            const numericValue = value.replace(/[^0-9]/g, "");
            setFormData({ ...formData, [name]: numericValue });
            return;
        }

        setFormData({
            ...formData,
            [name]: value
        });
    };

    const validateForm = () => {
        const {
            call_title,
            volume,
            issue,
            start_date,
            submission_deadline,
            review_date,
            publication_date
        } = formData;

        if (
            !call_title ||
            !volume ||
            !issue ||
            !start_date ||
            !submission_deadline ||
            !review_date ||
            !publication_date
        ) {
            setToast({
                show: true,
                message: "All fields are required",
                type: "error"
            });
            return false;
        }

        if (!/^\d+$/.test(volume)) {
            setToast({
                show: true,
                message: "Volume must be a number",
                type: "error"
            });
            return false;
        }

        if (!/^\d+$/.test(issue)) {
            setToast({
                show: true,
                message: "Issue must be a number",
                type: "error"
            });
            return false;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const startDate = new Date(start_date);
        const deadline = new Date(submission_deadline);
        const review = new Date(review_date);
        const publication = new Date(publication_date);

        if (startDate < today) {
            setToast({
                show: true,
                message: "Start date cannot be in the past",
                type: "error"
            });
            return false;
        }

        if (deadline < startDate) {
            setToast({
                show: true,
                message: "Submission deadline cannot be before start date",
                type: "error"
            });
            return false;
        }

        if (review < deadline) {
            setToast({
                show: true,
                message: "Review date cannot be before submission deadline",
                type: "error"
            });
            return false;
        }

        if (publication < review) {
            setToast({
                show: true,
                message: "Publication date cannot be before review date",
                type: "error"
            });
            return false;
        }

        return true;
    };

    const fetchCallForPapers = async () => {
        // const token = localStorage.getItem("adminToken");

        try {
            const response = await fetch(`${ApiUrl}/call-for-papers`);

            const data = await response.json();
            console.log("API Response:", data);

            if (!response.ok) {
                throw new Error(data.message);
            }

            if (Array.isArray(data) && data.length > 0) {
                const item = data[0];

                setFormData({
                    call_title: item.call_title || "",
                    start_date: item.start_date?.split("T")[0] || "",
                    volume: item.volume || "",
                    issue: item.issue || "",
                    submission_deadline: item.submission_deadline?.split("T")[0] || "",
                    review_date: item.review_date?.split("T")[0] || "",
                    publication_date: item.publication_date?.split("T")[0] || "",
                });

                setEditingId(item.id);
            }

        } catch (error) {
            console.error("Fetch Error:", error);
        }
    };

    useEffect(() => {
        fetchCallForPapers();
    }, []);


    const handleEdit = (item) => {
        setFormData({
            call_title: item.call_title,
            start_date: item.start_date,
            volume: item.volume,
            issue: item.issue,
            submission_deadline: item.submission_deadline,
            review_date: item.review_date,
            publication_date: item.publication_date,
        });

        setEditingId(item.id);
        setIsEditing(true);
    };


    const handleSave = async () => {
        if (!validateForm()) return;

        const token = localStorage.getItem("adminToken");
        setSaving(true)
        try {
            let response;

            if (editingId) {
                console.log("Form data", formData)
                // UPDATE
                response = await fetch(`${ApiUrl}/call-for-papers/${editingId}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(formData),

                });
            } else {
                // CREATE
                console.log("Form data", formData)

                response = await fetch(`${ApiUrl}/call-for-papers`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(formData),
                });
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Something went wrong");
            }

            setSuccessMessage(
                editingId
                    ? "Call for Papers updated successfully"
                    : "Call for Papers created successfully"
            );

            setIsEditing(false);
            setEditingId(null);
            fetchCallForPapers(); // refresh list

            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setSaving(false)
        }
    };

    const handleCancel = () => {
        setFormData({ ...formData });
        setIsEditing(false);
        setErrorMessage('');
    };

    const fetchMarquee = async () => {

        const response = await fetch(`${ApiUrl}/marquee-title`);
        const data = await response.json();
        console.log("Data", data)

        if (data?.id) {
            setMarqueeId(data.id);
            setDisplayedMarquee(data.marquee_text);
            // setMarqueeText(data.marquee_text);
        }
    };

    useEffect(() => {
        fetchMarquee();
    }, []);


    const handleMarqueeSubmit = async (e) => {
        e.preventDefault();

        if (!marqueeText.trim()) {
            setErrorMessage("Please enter marquee text");
            setTimeout(() => {
                setErrorMessage("");
            }, 3000);

            return;
        }

        const token = localStorage.getItem("adminToken");
        setSaving(true)
        try {
            let response;

            if (isMarqueeEditing && marqueeId) {
                // UPDATE
                response = await fetch(`${ApiUrl}/marquee-title/${marqueeId}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ marquee_title: marqueeText }),
                });
            } else {
                // CREATE
                response = await fetch(`${ApiUrl}/marquee-title`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ marquee_title: marqueeText }),
                });
            }

            const data = await response.json();
            if (!response.ok) throw new Error(data.message);

            setSuccessMessage(data.message);
            setDisplayedMarquee(marqueeText);
            setMarqueeText("");
            setIsMarqueeEditing(false);
            fetchMarquee();
            setTimeout(() => setSuccessMessage(""), 3000);


        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setSaving(false)

        }
    };

    const handleMarqueeEdit = () => {
        setMarqueeText(displayedMarquee);
        setIsMarqueeEditing(true);
    };
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    return (
        <div className="cfp-container">
            {/* Header */}
            <div className="page-header">
                <div className="cfp-title-wrapper">
                    <div className="title-icon">
                        <FileText size={24} />
                    </div>
                    <div>
                        <h1 className="cfp-title">Call for Papers</h1>
                        <p className="cfp-subtitle">Manage your journal's call for papers</p>
                    </div>
                </div>

                {!isEditing ? (
                    <button className="cfp-edit-btn" onClick={() => setIsEditing(true)}>
                        <Edit2 size={16} />
                        <span>Edit</span>
                    </button>
                ) : (
                    <div className="cfp-edit-actions">
                        <button className="cfp-cancel-btn" onClick={handleCancel} disabled={saving}>
                            <X size={16} />
                            <span>Cancel</span>
                        </button>
                        <button className="cfp-save-btn" onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <>
                                    <Loader2 size={16} className="spin-icon" />
                                    <span>{editingId ? 'Updating...' : 'Saving...'}</span>
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    <span>{editingId ? 'Update' : 'Save'}</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Messages */}
            {successMessage && (
                <div className="cfp-message success">
                    <CheckCircle size={18} />
                    <span>{successMessage}</span>
                </div>
            )}

            {errorMessage && (
                <div className="cfp-message error">
                    <AlertCircle size={18} />
                    <span>{errorMessage}</span>
                </div>
            )}

            {/* Main Card */}
            <div className="cfp-card">
                <div className="cfp-row title-row">
                    <div className="cfp-field title-field">
                        <label className="cfp-label">
                            <FileText size={16} />
                            <span>Call Title <span className="required">*</span></span>
                        </label>
                        {isEditing ? (
                            <input
                                type="text"
                                name="call_title"
                                value={formData.call_title}
                                onChange={handleInputChange}
                                className="cfp-input"
                                placeholder="Enter call title"
                            />
                        ) : (
                            <div className="cfp-value title-value">{formData.call_title || "Your Title Here"}</div>
                        )}
                    </div>

                    <div className="cfp-field date-field">
                        <label className="cfp-label">
                            <Calendar size={16} />
                            <span>Start Date <span className="required">*</span></span>
                        </label>
                        {isEditing ? (
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleInputChange}
                                className="cfp-input"
                            />
                        ) : (
                            <div className="cfp-value date-value">{formatDate(formData.start_date)}</div>
                        )}
                    </div>
                </div>

                {/* Volume and Issue */}
                <div className="cfp-row">
                    <div className="cfp-field">
                        <label className="cfp-label">
                            <Hash size={16} />
                            <span>Volume <span className="required">*</span></span>
                        </label>
                        {isEditing ? (
                            <input
                                type="text"
                                name="volume"
                                value={formData.volume}
                                onChange={handleInputChange}
                                className="cfp-input"
                                placeholder="Volume"
                            />
                        ) : (
                            <div className="cfp-value">Vol. {formData.volume}</div>
                        )}
                    </div>

                    <div className="cfp-field">
                        <label className="cfp-label">
                            <Hash size={16} />
                            <span>Issue <span className="required">*</span></span>
                        </label>
                        {isEditing ? (
                            <input
                                type="text"
                                name="issue"
                                value={formData.issue}
                                onChange={handleInputChange}
                                className="cfp-input"
                                placeholder="Issue"
                            />
                        ) : (
                            <div className="cfp-value">Issue {formData.issue}</div>
                        )}
                    </div>
                </div>

                {/* Important Dates */}
                <div className="cfp-dates-section">
                    <h3 className="cfp-section-title">Important Dates</h3>
                    <div className="cfp-dates-grid">
                        <div className="cfp-date-card">
                            <div className="cfp-date-icon deadline">
                                <Clock size={20} />
                            </div>
                            <div className="cfp-date-content">
                                <span className="cfp-date-label">Deadline <span className="required">*</span></span>
                                {isEditing ? (
                                    <input
                                        type="date"
                                        name="submission_deadline"
                                        value={formData.submission_deadline}
                                        onChange={handleInputChange}
                                        className="cfp-date-input"
                                    />
                                ) : (
                                    <span className="cfp-date-value">{formatDate(formData.submission_deadline)}</span>
                                )}
                            </div>
                        </div>

                        <div className="cfp-date-card">
                            <div className="cfp-date-icon review">
                                <FileEdit size={20} />
                            </div>
                            <div className="cfp-date-content">
                                <span className="cfp-date-label">Review Date <span className="required">*</span></span>
                                {isEditing ? (
                                    <input
                                        type="date"
                                        name="review_date"
                                        value={formData.review_date}
                                        onChange={handleInputChange}
                                        className="cfp-date-input"
                                    />
                                ) : (
                                    <span className="cfp-date-value">{formatDate(formData.review_date)}</span>
                                )}
                            </div>
                        </div>

                        <div className="cfp-date-card">
                            <div className="cfp-date-icon publication">
                                <CalendarDays size={20} />
                            </div>
                            <div className="cfp-date-content">
                                <span className="cfp-date-label">Publication <span className="required">*</span></span>
                                {isEditing ? (
                                    <input
                                        type="date"
                                        name="publication_date"
                                        value={formData.publication_date}
                                        onChange={handleInputChange}
                                        className="cfp-date-input"
                                    />
                                ) : (
                                    <span className="cfp-date-value">{formatDate(formData.publication_date)}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Timeline (non-editable view only) */}
                {!isEditing && (
                    <div className="cfp-timeline">
                        <div className="cfp-timeline-track">
                            <div className="cfp-timeline-item">
                                <div className="cfp-timeline-dot start"></div>
                                <div className="cfp-timeline-content">
                                    <span className="cfp-timeline-label">Start</span>
                                    <span className="cfp-timeline-date">{formatDate(formData.start_date)}</span>
                                </div>
                            </div>
                            <div className="cfp-timeline-line"></div>
                            <div className="cfp-timeline-item">
                                <div className="cfp-timeline-dot deadline"></div>
                                <div className="cfp-timeline-content">
                                    <span className="cfp-timeline-label">Deadline</span>
                                    <span className="cfp-timeline-date">{formatDate(formData.submission_deadline)}</span>
                                </div>
                            </div>
                            <div className="cfp-timeline-line"></div>
                            <div className="cfp-timeline-item">
                                <div className="cfp-timeline-dot review"></div>
                                <div className="cfp-timeline-content">
                                    <span className="cfp-timeline-label">Review</span>
                                    <span className="cfp-timeline-date">{formatDate(formData.review_date)}</span>
                                </div>
                            </div>
                            <div className="cfp-timeline-line"></div>
                            <div className="cfp-timeline-item">
                                <div className="cfp-timeline-dot publication"></div>
                                <div className="cfp-timeline-content">
                                    <span className="cfp-timeline-label">Publication</span>
                                    <span className="cfp-timeline-date">{formatDate(formData.publication_date)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="cfp-marquee-card">
                <div className="cfp-marquee-header">
                    <h3 className="cfp-marquee-title">Marquee</h3>


                </div>

                <form onSubmit={handleMarqueeSubmit} className="cfp-marquee-form">
                    <input
                        type="text"
                        placeholder="Enter marquee text..."
                        value={marqueeText}
                        onChange={(e) => setMarqueeText(e.target.value)}
                        className="cfp-marquee-input"
                        required
                    />
                    <button type="submit" className="cfp-marquee-btn" disabled={saving}>
                        {/* <Send size={16} /> */}
                        {/* <span>{isMarqueeEditing ? "Update" : "Add"}</span> */}
                        {saving ? (
                            <>
                                <Loader2 size={16} className="spin-icon" />
                                <span>{isMarqueeEditing ? 'Updating...' : 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <Save size={16} />
                                <span>{isMarqueeEditing ? 'Update' : 'Save'}</span>
                            </>
                        )}
                    </button>
                    {displayedMarquee && !isMarqueeEditing && (
                        <button
                            className="cfp-edit-btn"
                            onClick={handleMarqueeEdit}
                            disabled={saving}
                        >
                            <Edit2 size={16} />
                            <span>Edit</span>
                        </button>
                    )}

                    {isMarqueeEditing && (

                        <button
                            type="button"
                            className="cfp-cancel-btn"
                            disabled={saving}
                            onClick={() => {
                                setIsMarqueeEditing(false);
                                setMarqueeText("");
                            }}
                        >
                            <X size={16} />
                            <span>Cancel</span>
                        </button>
                    )}
                </form>

                {displayedMarquee && (
                    <div className="cfp-marquee-preview">
                        <div className="cfp-marquee-static">
                            <span className="cfp-marquee-badge">Preview</span>
                            <span>{displayedMarquee}</span>

                        </div>

                    </div>
                )}
            </div>
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                />
            )}
        </div>
    );
};

export default CallForPapers;